import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toastr } from "react-redux-toastr";
import { API_URL } from "../../utils/constant";
import Pagination from "../Pagination/Pagination";

// Custom styles for enhanced table appearance
const tableStyles = `
  .enhanced-table {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: none;
  }
  
  .enhanced-table tbody tr {
    transition: all 0.3s ease;
    border-left: 4px solid transparent;
  }
  
  .enhanced-table tbody tr:hover {
    background-color: #f8f9fa;
    border-left-color: #667eea;
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .enhanced-table tbody td {
    padding: 16px 12px;
    vertical-align: middle;
    border-top: 1px solid #e9ecef;
  }
  
  .enhanced-table .badge {
    font-weight: 500;
    letter-spacing: 0.3px;
  }
  
  .scan-status-item {
    background: #f8f9fa;
    border-radius: 6px;
    padding: 6px 10px;
    margin-bottom: 4px;
    border-left: 3px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .scan-status-item:hover {
    background: #e9ecef;
    border-left-color: #667eea;
  }
  
  .action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  
  .btn-link {
    background: none;
    border: none;
    color: #007bff;
    text-decoration: none;
    padding: 6px 12px;
    border-radius: 4px;
    transition: all 0.2s ease;
    cursor: pointer;
  }
  
  .btn-link:hover {
    background-color: #f8f9fa;
    color: #0056b3;
    text-decoration: none;
  }
  
  .btn-link:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .empty-state {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border-radius: 12px;
    padding: 40px 20px;
  }
  
  .loading-state {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    border-radius: 12px;
    padding: 40px 20px;
  }
  
  .modal-xl .modal-content {
    border-radius: 12px;
    border: none;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
  
  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: none;
    border-radius: 12px 12px 0 0;
  }
  
  .modal-header .close {
    color: white;
    opacity: 0.8;
  }
  
  .modal-header .close:hover {
    opacity: 1;
  }
`;

const RedTeamingScans = () => {
    // State Management
    const [scans, setScans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedScan, setSelectedScan] = useState(null);
    const [scanDetails, setScanDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [downloadingReport, setDownloadingReport] = useState(false);
    const [downloadingScanReport, setDownloadingScanReport] = useState(false);
    const [downloadingScans, setDownloadingScans] = useState({});  // Track download state for individual scans
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [filters, setFilters] = useState({});
    const [sorting, setSorting] = useState({
        sortBy: 'updatedAt',
        sortOrder: 'desc'
    });
    const [modalPagination, setModalPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });

    const { orgId } = useParams();
    const id = useSelector(state => {
        if (orgId) {
            return orgId;
        } else {
            return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
        }
    });

    const handleApiError = (error, defaultMessage = "An error occurred") => {
        // console.error removed
        let message = defaultMessage;

        if (error.response) {
            message = error.response.data?.message || error.response.data?.userMessage || message;
        } else if (error.message) {
            message = error.message;
        }

        toastr.error('Error', message);
    };

    const fetchScans = async (page = 1) => {
        try {
            setLoading(true);
            if (!id) {
                toastr.error('Error', 'Organisation ID not found');
                return;
            }

            const response = await axios.get(
                `${API_URL}/organisations/${id}/cart/stimulation-scans`,
                {
                    params: {
                        limit: pagination.limit,
                        page: page,
                        sortBy: sorting.sortBy,
                        sortOrder: sorting.sortOrder,
                        ...filters
                    }
                }
            );


            if (response.data?.data?.stimulationScans) {
                setScans(response.data.data.stimulationScans || []);

                // Extract pagination data from API response
                if (response.data.data.pagination) {
                    setPagination({
                        currentPage: response.data.data.pagination.currentPage || page,
                        totalPages: response.data.data.pagination.totalPages || 1,
                        totalCount: response.data.data.pagination.totalCount || 0,
                        limit: response.data.data.pagination.limit || pagination.limit,
                        hasNextPage: response.data.data.pagination.hasNextPage || false,
                        hasPrevPage: response.data.data.pagination.hasPrevPage || false
                    });
                } else {
                    setPagination({
                        ...pagination,
                        currentPage: page,
                    });
                }
            } else {
                setScans([]);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch stimulation scans");
            setScans([]);
            setPagination({
                currentPage: 1,
                totalPages: 1,
                totalCount: 0,
                limit: 10,
                hasNextPage: false,
                hasPrevPage: false
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchScans(page);
    };

    const handleViewDetails = async (scan, page = 1) => {
        try {
            setLoadingDetails(true);
            setSelectedScan(scan);
            setModalVisible(true);

            const response = await axios.get(
                `${API_URL}/organisations/${id}/cart/issues`,
                {
                    params: {
                        scanId: scan._id,
                        page: page,
                        limit: modalPagination.limit
                    }
                }
            );

            if (response.data?.data?.issues) {
                setScanDetails(response.data.data);
                // Update modal pagination
                if (response.data.data.pagination) {
                    setModalPagination(response.data.data.pagination);
                }
            } else {
                toastr.error('Error', 'No details found');
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch scan details");
            setScanDetails(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleModalPageChange = (page) => {
        if (selectedScan) {
            handleViewDetails(selectedScan, page);
        }
    };

    const handleCloseModal = () => {
        setModalVisible(false);
        setSelectedScan(null);
        setScanDetails(null);
        setModalPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPrevPage: false
        });
    };

    const downloadReport = async (scanId = null) => {
        try {
            if (scanId) {
                if (downloadingScans[scanId]) return; // Prevent multiple downloads
                setDownloadingScans(prev => ({ ...prev, [scanId]: true }));
            } else {
                setDownloadingReport(true);
            }

            const url = scanId
                ? `${API_URL}/organisations/${id}/cart/issues/report?scanId=${scanId}`
                : `${API_URL}/organisations/${id}/cart/issues/report`;

            const response = await axios.get(url, {
                responseType: 'blob'
            });

            // Create blob link to download
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const href = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', `cart-issues-report${scanId ? `-scan-${scanId}` : ''}.pdf`);
            document.body.appendChild(link);
            link.click();

            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
            toastr.success('Success', 'Report downloaded successfully');
        } catch (error) {
            handleApiError(error, "Failed to download report");
        } finally {
            if (scanId) {
                setDownloadingScans(prev => ({ ...prev, [scanId]: false }));
            } else {
                setDownloadingReport(false);
            }
        }
    };

    useEffect(() => {
        if (id) {
            fetchScans(pagination.currentPage);
        } else {
        }
    }, [id]);

    // Debug effect to log scans state changes
    useEffect(() => {
    }, [scans]);

    const getScanStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'badge-success';
            case 'failed':
                return 'badge-danger';
            case 'inprogress':
                return 'badge-warning';
            case 'pending':
                return 'badge-info';
            default:
                return 'badge-secondary';
        }
    };

    const getScanStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'fa-check-circle';
            case 'failed':
                return 'fa-times-circle';
            case 'inprogress':
                return 'fa-spinner fa-spin';
            case 'pending':
                return 'fa-clock-o';
            default:
                return 'fa-question-circle';
        }
    };

    const getSeverityBadgeClass = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'info':
                return 'badge-info';
            case 'low':
                return 'badge-success';
            case 'medium':
                return 'badge-dark';
            case 'high':
                return 'badge-danger';
            case 'critical':
                return 'badge-dark';
            default:
                return 'badge-secondary';
        }
    };

    return (
        <div className="container-fluid">
            <style>{tableStyles}</style>
            <div className="block-header">
                <div className="row clearfix">
                    <div className="col-md-6 col-sm-12">
                        <h1 className="welcome-text">Simulation Scans</h1>
                        <nav aria-label="breadcrumb" className="mt-1">
                            <ol className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/" className="welcome-textSmall">Home</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to="/red-teaming/dashboard">Red Teaming Dashboard</Link>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page"
                                    style={{ color: "white" }}
                                >
                                    Simulation Scans
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card enhanced-table">
                        <div className="card-body p-0">
                            <div className="row mb-3 p-3">
                                <div className="col-md-6">
                                    <h4 className="header-title mb-0">
                                        <i className="fa fa-search fa-2x mr-2 text-primary"></i>
                                        Simulation Scans
                                    </h4>
                                    <small className="text-muted">Monitor and manage your red teaming simulation scans</small>
                                </div>
                                <div className="col-md-6 text-right">
                                    <div className="d-flex justify-content-end align-items-center">
                                        <span className="text-muted mr-3">
                                            Total Scans: <strong>{pagination.totalCount}</strong>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-center" style={{width: '60px'}}>#</th>
                                            <th style={{minWidth: '150px'}}>Target</th>
                                            <th style={{width: '120px'}}>Target Type</th>
                                            <th style={{minWidth: '200px'}}>Scheduler Name</th>
                                            <th style={{width: '120px'}}>Status</th>
                                            <th style={{minWidth: '300px'}}>Simulation Scans</th>
                                            <th style={{width: '120px'}}>Created</th>
                                            <th style={{width: '120px'}}>Updated</th>
                                            {/* <th style={{width: '150px'}}>Actions</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="loading-state">
                                                        <div className="d-flex flex-column align-items-center">
                                                            <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                                                                <span className="sr-only">Loading...</span>
                                                            </div>
                                                            <span className="text-muted font-weight-bold">Loading simulation scans...</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : scans && scans.length > 0 ? (
                                            scans.map((scan, index) => {
                                                return (
                                                    <tr key={scan.stimulationScanId || scan._id || index} className="align-middle">
                                                        <td className="text-center">
                                                            <span className="badge badge-light badge-pill font-weight-bold">
                                                                {(pagination.currentPage - 1) * pagination.limit + index + 1}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="mr-2">
                                                                    <i className="fa fa-target fa-lg text-info"></i>
                                                                </div>
                                                                <div>
                                                                    <strong className="text-dark">{scan.target || 'N/A'}</strong>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className="badge badge-outline-info badge-pill px-3 py-2">
                                                                <i className="fa fa-tag mr-1"></i>
                                                                {scan.targetType || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="mr-2">
                                                                    <i className="fa fa-calendar fa-lg text-primary"></i>
                                                                </div>
                                                                <div>
                                                                    <strong className="text-dark">{scan.stimulationSchedulerName || 'N/A'}</strong>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <span className={`badge badge-pill px-3 py-2 ${getScanStatusBadgeClass(scan.scanStatus)}`}>
                                                                <i className={`fa ${getScanStatusIcon(scan.scanStatus)} mr-1`}></i>
                                                                {scan.scanStatus?.toUpperCase() || 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column gap-2">
                                                                {scan.stimulationScanStatus && Array.isArray(scan.stimulationScanStatus) ? (
                                                                    scan.stimulationScanStatus.map((status, statusIndex) => (
                                                                        <div key={`${scan.stimulationScanId || scan._id}-${status.stimulationName}-${statusIndex}`} className="scan-status-item">
                                                                            <div className="d-flex align-items-center">
                                                                                <i className="fa fa-shield mr-2 text-muted"></i>
                                                                                <span className="font-weight-bold text-dark" style={{fontSize: '0.9rem'}}>
                                                                                    {status.stimulationName || 'Unknown'}
                                                                                </span>
                                                                            </div>
                                                                            <span className={`badge ${getScanStatusBadgeClass(status.stimulationScanStatus)} badge-pill px-3 py-2`}>
                                                                                <i className={`fa ${getScanStatusIcon(status.stimulationScanStatus)} mr-1`}></i>
                                                                                {status.stimulationScanStatus?.toUpperCase() || 'N/A'}
                                                                            </span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <span className="text-muted">
                                                                        <i className="fa fa-info-circle mr-1"></i>
                                                                        No simulation status data
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="text-muted">
                                                                <div>{new Date(scan.createdAt).toLocaleDateString()}</div>
                                                                <small>{new Date(scan.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</small>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="text-muted">
                                                                <div>{new Date(scan.updatedAt).toLocaleDateString()}</div>
                                                                <small>{new Date(scan.updatedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</small>
                                                            </div>
                                                        </td>
                                                        
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <div className="d-flex flex-column align-items-center">
                                                            <i className="fa fa-search fa-3x text-muted mb-3"></i>
                                                            <h5 className="text-muted mb-2">No simulation scans found</h5>
                                                            <p className="text-muted mb-0">Run simulations to generate scan results</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {pagination.totalCount > 0 && (
                                <div className="row mt-3 p-3">
                                    <div className="col-md-12 d-flex justify-content-end">
                                        <div className="dataTables_paginate">
                                            <Pagination
                                                className="pagination-bar"
                                                currentPage={pagination.currentPage}
                                                totalCount={pagination.totalCount}
                                                pageSize={pagination.limit}
                                                onPageChange={handlePageChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            <div className={`modal fade ${modalVisible ? 'show' : ''}`}
                style={{ display: modalVisible ? 'block' : 'none' }}
                tabIndex="-1"
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                <i className="fa fa-search mr-2"></i>
                                Scan Details
                            </h5>
                            <div>
                                <button type="button" className="close" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                        </div>
                        <div className="modal-body">
                            {loadingDetails ? (
                                <div className="loading-state">
                                    <div className="d-flex flex-column align-items-center">
                                        <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                        <span className="text-muted font-weight-bold">Loading scan details...</span>
                                    </div>
                                </div>
                            ) : scanDetails?.issues ? (
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-center" style={{width: '60px'}}>#</th>
                                                <th style={{minWidth: '150px'}}>Simulation Name</th>
                                                <th style={{minWidth: '120px'}}>Target</th>
                                                <th style={{width: '120px'}}>Target Type</th>
                                                <th style={{minWidth: '200px'}}>Issue Name</th>
                                                <th style={{width: '100px'}}>Severity</th>
                                                <th style={{minWidth: '300px'}}>Description</th>
                                                <th style={{width: '120px'}}>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scanDetails.issues.map((issue, index) => (
                                                <tr key={issue.issueId} className="align-middle">
                                                    <td className="text-center">
                                                        <span className="badge badge-light badge-pill font-weight-bold">
                                                            {index + 1}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <i className="fa fa-shield mr-2 text-primary"></i>
                                                            <strong className="text-dark">{issue.stimulationName || 'N/A'}</strong>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-outline-info badge-pill px-3 py-2">
                                                            <i className="fa fa-target mr-1"></i>
                                                            {issue.target || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-outline-secondary badge-pill px-3 py-2">
                                                            <i className="fa fa-tag mr-1"></i>
                                                            {issue.targetType || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div style={{ maxWidth: '300px', whiteSpace: 'normal' }}>
                                                            <strong className="text-dark">{issue.issueName || 'N/A'}</strong>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className={`badge badge-pill px-3 py-2 ${getSeverityBadgeClass(issue.issueSeverity)}`}>
                                                            <i className={`fa ${
                                                                issue.issueSeverity === 'info' ? 'fa-info-circle' :
                                                                issue.issueSeverity === 'low' ? 'fa-check-circle' :
                                                                issue.issueSeverity === 'medium' ? 'fa-exclamation-triangle' :
                                                                issue.issueSeverity === 'high' ? 'fa-times-circle' :
                                                                'fa-question-circle'
                                                            } mr-1`}></i>
                                                            {issue.issueSeverity?.toUpperCase() || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td style={{ whiteSpace: 'normal', width: '300px', textWrap: 'wrap' }}>
                                                        <div className="text-muted" style={{fontSize: '0.9rem', lineHeight: '1.4'}}>
                                                            {issue.issueDescription || 'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="text-muted">
                                                            <div>{new Date(issue.createdAt).toLocaleDateString()}</div>
                                                            <small>{new Date(issue.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</small>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {/* Modal Pagination */}
                                    {scanDetails.pagination && scanDetails.pagination.totalCount > 0 && (
                                        <div className="row mt-3">
                                            <div className="col-md-12 d-flex justify-content-end">
                                                <div className="dataTables_paginate">
                                                    <Pagination
                                                        className="pagination-bar"
                                                        currentPage={scanDetails.pagination.currentPage}
                                                        totalCount={scanDetails.pagination.totalCount}
                                                        pageSize={scanDetails.pagination.limit}
                                                        onPageChange={handleModalPageChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="d-flex flex-column align-items-center">
                                        <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                                        <h5 className="text-muted mb-2">No issues found</h5>
                                        <p className="text-muted mb-0">This scan completed without finding any security issues</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {modalVisible && <div className="modal-backdrop fade show"></div>}
        </div>
    );
};

export default RedTeamingScans;
